/**
 * AlertsPage - gestion des alertes (notifications email sur evenements).
 * Disponible a partir du plan Pro.
 *
 * - Si trial / starter : affiche un upsell + apercu desactive
 * - Si pro+ : settings actifs (a connecter au backend - placeholder pour l instant)
 */
import { useEffect, useState } from 'react';
import {
  Card, CardHeader, Title3, Body1, Caption1, Switch, tokens,
  MessageBar, MessageBarBody, MessageBarTitle,
  Button, Input, Label, Spinner,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions, DialogTrigger,
  Dropdown, Option, Field,
} from '@fluentui/react-components';
import {
  Alert24Regular, Mail24Regular, Warning24Regular, ArrowTrendingDown24Regular,
  ShieldCheckmark24Regular, Sparkle24Filled, Send24Regular, CheckmarkCircle24Regular,
  Edit20Regular, Dismiss16Regular, Add20Regular, ErrorCircle24Regular,
  Send20Regular,
} from '@fluentui/react-icons';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../components/PageHeader';
import { useLicense } from '../auth/LicenseGate';
import { useAuth } from '../auth/AuthContext';
import { alertsApi, type TestEmailResponse, type AlertRule, type AlertRuleType, type AlertFrequency } from '../api/alertsApi';

// Alertes : reserve Business+ (cf. pricing matrix v2026.06)
const BUSINESS_OR_HIGHER = new Set(['business', 'enterprise']);

export function AlertsPage() {
  const { t } = useTranslation();
  const license = useLicense();
  // Normalise lowercase pour tolerer les cles avec casse historique ('Business' vs 'business')
  const currentPlan = (license.status?.plan ?? '').toLowerCase();
  // Alertes Business+ : on garde le nom local hasProPlan pour eviter de toucher
  // les passages internes (props, opacity, disabled). Semantiquement c est
  // maintenant "a au moins Business" - voir le set BUSINESS_OR_HIGHER ci-dessus.
  const hasProPlan = Boolean(license.hasLicense && license.status?.valid && BUSINESS_OR_HIGHER.has(currentPlan));

  return (
    <div>
      <PageHeader
        title={t('alerts.title')}
        description={t('alerts.subtitle')}
      />

      {/* Upsell banner si pas Pro+ */}
      {!hasProPlan && <ProUpsellBanner currentPlan={currentPlan} />}

      <div style={{ display: 'grid', gap: 16, marginTop: 16 }}>
        {/* Email notifications - regles persistantes via API */}
        <AlertRulesSection hasProPlan={hasProPlan} />

        {/* Test email - dispo meme en trial pour valider l infra ACS */}
        <TestEmailSection />

        {/* Webhooks (Pro+) */}
        <Card style={{ opacity: hasProPlan ? 1 : 0.55 }}>
          <CardHeader
            image={<Alert24Regular primaryFill={tokens.colorBrandForeground1}/>}
            header={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Title3>{t('alerts.webhooks.title')}</Title3>
                <span style={{
                  background: tokens.colorNeutralBackground3,
                  color: tokens.colorNeutralForeground2,
                  padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                }}>{t('tag.comingSoon')}</span>
              </div>
            }
          />
          <Body1 style={{ display: 'block' }}>
            {t('alerts.webhooks.description')}
          </Body1>
          {/* v2026.06 - footnote "newsletter prochaine release" retiree */}
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AlertRulesSection - liste des regles d alerte avec edition par regle.
// Charge depuis /api/alerts/rules au mount. Permet d activer/desactiver
// et d ouvrir une modal d edition (recipients, threshold, frequency).
// ---------------------------------------------------------------------------
const RULE_META: Record<AlertRuleType, { icon: React.ReactNode; titleKey: string; bodyKey: string }> = {
  scoreRegression: { icon: <ArrowTrendingDown24Regular />, titleKey: 'alerts.alert.regressionTitle',  bodyKey: 'alerts.alert.regressionBody' },
  criticalFail:    { icon: <Warning24Regular />,           titleKey: 'alerts.alert.criticalFailTitle',bodyKey: 'alerts.alert.criticalFailBody' },
  errorSpike:      { icon: <ErrorCircle24Regular />,       titleKey: 'alerts.alert.errorSpikeTitle',  bodyKey: 'alerts.alert.errorSpikeBody' },
  weeklyDigest:    { icon: <ShieldCheckmark24Regular />,   titleKey: 'alerts.alert.weeklyDigestTitle',bodyKey: 'alerts.alert.weeklyDigestBody' },
};

function AlertRulesSection({ hasProPlan }: { hasProPlan: boolean }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [rules, setRules] = useState<AlertRule[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AlertRule | null>(null);
  const [testingType, setTestingType] = useState<AlertRuleType | null>(null);
  const [testSuccess, setTestSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!hasProPlan) { setLoading(false); return; }
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasProPlan]);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const r = await alertsApi.listRules();
      setRules(r);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function toggleRule(rule: AlertRule, enabled: boolean) {
    // Si on active une regle sans recipients, on prefille avec l email du user
    const recipients = enabled && rule.recipients.length === 0 && user?.email
      ? [user.email]
      : rule.recipients;
    try {
      const updated = await alertsApi.upsertRule({ ...rule, enabled, recipients });
      setRules((rs) => (rs ?? []).map((r) => r.type === rule.type ? updated : r));
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function saveRule(updated: AlertRule) {
    try {
      const saved = await alertsApi.upsertRule(updated);
      setRules((rs) => (rs ?? []).map((r) => r.type === saved.type ? saved : r));
      setEditing(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function testRule(rule: AlertRule) {
    setTestingType(rule.type);
    setTestSuccess(null);
    setError(null);
    try {
      // Si la regle a des recipients : on les utilise tels quels.
      // Sinon : le backend fallback sur user.email.
      const recipients = rule.recipients?.length ? rule.recipients : undefined;
      const r = await alertsApi.testRule(rule.type, recipients);
      setTestSuccess(t('alerts.test.success', { count: r.recipients.length }));
      // Auto-hide success message apres 5s
      setTimeout(() => setTestSuccess(null), 5000);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setTestingType(null);
    }
  }

  return (
    <Card style={{ opacity: hasProPlan ? 1 : 0.55 }}>
      <CardHeader
        image={<Mail24Regular primaryFill={tokens.colorBrandForeground1}/>}
        header={<Title3>{t('alerts.email.title')}</Title3>}
      />
      <Body1 style={{ marginBottom: 16, display: 'block' }}>
        {t('alerts.email.subtitle')}
      </Body1>

      {loading && (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Spinner size="small" />
        </div>
      )}

      {error && (
        <MessageBar intent="error" style={{ marginBottom: 12 }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {testSuccess && (
        <MessageBar intent="success" style={{ marginBottom: 12 }}>
          <MessageBarBody>{testSuccess}</MessageBarBody>
        </MessageBar>
      )}

      {!loading && rules && rules.map((rule) => {
        const meta = RULE_META[rule.type];
        return (
          <div
            key={rule.type}
            style={{
              display:      'flex',
              alignItems:   'flex-start',
              gap:          14,
              padding:      '14px 4px',
              borderTop:    `1px solid ${tokens.colorNeutralStroke2}`,
            }}
          >
            <span style={{ color: tokens.colorBrandForeground1, flexShrink: 0, marginTop: 2 }}>
              {meta.icon}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Body1 style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>{t(meta.titleKey)}</Body1>
              <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>{t(meta.bodyKey)}</Caption1>
              {/* Recipients summary */}
              {rule.enabled && (
                <Caption1 style={{ color: tokens.colorNeutralForeground2, display: 'block', marginTop: 6, fontSize: 12 }}>
                  {rule.recipients.length === 0
                    ? t('alerts.rule.noRecipients')
                    : t('alerts.rule.recipientsCount', { count: rule.recipients.length, list: rule.recipients.slice(0, 2).join(', ') + (rule.recipients.length > 2 ? '…' : '') })}
                  {' · '}
                  {t(`alerts.rule.frequency.${rule.frequency}`)}
                  {rule.lastTriggeredAt && (
                    <> {' · '}{t('alerts.rule.lastTriggered', { date: new Date(rule.lastTriggeredAt).toLocaleDateString() })}</>
                  )}
                </Caption1>
              )}
            </div>
            <Button
              appearance="subtle"
              icon={testingType === rule.type ? <Spinner size="tiny" /> : <Send20Regular />}
              onClick={() => testRule(rule)}
              disabled={!hasProPlan || testingType !== null}
              title={t('alerts.rule.test')}
              style={{ flexShrink: 0 }}
            >
              {t('alerts.rule.test')}
            </Button>
            <Button
              appearance="subtle"
              icon={<Edit20Regular />}
              onClick={() => setEditing(rule)}
              disabled={!hasProPlan}
              title={t('alerts.rule.edit')}
              style={{ flexShrink: 0 }}
            />
            <Switch
              checked={rule.enabled}
              disabled={!hasProPlan}
              onChange={(_, d) => toggleRule(rule, d.checked)}
            />
          </div>
        );
      })}

      {/* Edit dialog */}
      {editing && (
        <AlertRuleEditDialog
          rule={editing}
          onClose={() => setEditing(null)}
          onSave={saveRule}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// AlertRuleEditDialog - modal d edition d une regle.
// ---------------------------------------------------------------------------
function AlertRuleEditDialog({
  rule,
  onClose,
  onSave,
}: {
  rule: AlertRule;
  onClose: () => void;
  onSave: (r: AlertRule) => Promise<void>;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [recipients, setRecipients] = useState<string[]>(rule.recipients);
  const [recipientInput, setRecipientInput] = useState('');
  const [threshold, setThreshold] = useState<string>(
    rule.thresholdPoints != null ? String(rule.thresholdPoints) : ''
  );
  const [frequency, setFrequency] = useState<AlertFrequency>(rule.frequency);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const meta = RULE_META[rule.type];
  const needsThreshold = rule.type === 'scoreRegression' || rule.type === 'errorSpike';

  function addRecipient() {
    const email = recipientInput.trim();
    if (!email) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError(t('alerts.rule.invalidEmail'));
      return;
    }
    if (recipients.includes(email)) {
      setError(t('alerts.rule.duplicateEmail'));
      return;
    }
    setRecipients([...recipients, email]);
    setRecipientInput('');
    setError(null);
  }

  function removeRecipient(email: string) {
    setRecipients(recipients.filter((r) => r !== email));
  }

  async function onSubmit() {
    setSaving(true);
    setError(null);
    try {
      const payload: AlertRule = {
        ...rule,
        recipients,
        thresholdPoints: needsThreshold && threshold ? Number(threshold) : null,
        frequency,
      };
      await onSave(payload);
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <Dialog open={true} onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t(meta.titleKey)}</DialogTitle>
          <DialogContent>
            <Body1 block style={{ marginBottom: 16, color: tokens.colorNeutralForeground2 }}>
              {t(meta.bodyKey)}
            </Body1>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Recipients chips */}
              <Field
                label={t('alerts.rule.recipients')}
                hint={t('alerts.rule.recipientsHint', { email: user?.email ?? '' })}
              >
                <div style={{ display: 'flex', gap: 8 }}>
                  <Input
                    type="email"
                    value={recipientInput}
                    onChange={(_, d) => setRecipientInput(d.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(); } }}
                    placeholder={user?.email ?? 'admin@example.com'}
                    style={{ flex: 1 }}
                  />
                  <Button icon={<Add20Regular />} onClick={addRecipient}>
                    {t('alerts.rule.addRecipient')}
                  </Button>
                </div>
                {recipients.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {recipients.map((r) => (
                      <span
                        key={r}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '4px 10px',
                          background: tokens.colorBrandBackground2,
                          color: tokens.colorBrandForeground1,
                          borderRadius: 999,
                          fontSize: 13,
                          fontWeight: 500,
                        }}
                      >
                        {r}
                        <button
                          onClick={() => removeRecipient(r)}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            color: 'inherit',
                            display: 'flex',
                            padding: 0,
                          }}
                          title={t('common.delete')}
                        >
                          <Dismiss16Regular />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </Field>

              {/* Threshold (regression score / error spike uniquement) */}
              {needsThreshold && (
                <Field
                  label={
                    rule.type === 'scoreRegression'
                      ? t('alerts.rule.thresholdPoints')
                      : t('alerts.rule.thresholdErrors')
                  }
                  hint={
                    rule.type === 'scoreRegression'
                      ? t('alerts.rule.thresholdPointsHint')
                      : t('alerts.rule.thresholdErrorsHint')
                  }
                >
                  <Input
                    type="number"
                    min={1}
                    max={50}
                    value={threshold}
                    onChange={(_, d) => setThreshold(d.value)}
                  />
                </Field>
              )}

              {/* Frequency */}
              <Field label={t('alerts.rule.frequencyLabel')} hint={t('alerts.rule.frequencyHint')}>
                <Dropdown
                  value={t(`alerts.rule.frequency.${frequency}`)}
                  selectedOptions={[frequency]}
                  onOptionSelect={(_, d) => setFrequency((d.optionValue as AlertFrequency) ?? 'on-scan')}
                >
                  <Option value="on-scan">{t('alerts.rule.frequency.on-scan')}</Option>
                  <Option value="daily">{t('alerts.rule.frequency.daily')}</Option>
                  <Option value="weekly">{t('alerts.rule.frequency.weekly')}</Option>
                  <Option value="monthly">{t('alerts.rule.frequency.monthly')}</Option>
                </Dropdown>
              </Field>

              {error && (
                <MessageBar intent="error">
                  <MessageBarBody>{error}</MessageBarBody>
                </MessageBar>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={onClose} disabled={saving}>
                {t('common.cancel')}
              </Button>
            </DialogTrigger>
            <Button
              appearance="primary"
              onClick={onSubmit}
              disabled={saving || recipients.length === 0}
              icon={saving ? <Spinner size="tiny" /> : undefined}
            >
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// TestEmailSection - encart pour declencher un email de test via /api/alerts/test-email.
//
// Pas verrouille par le plan : meme en trial on peut tester la chaine ACS
// pour valider que le compte recevra bien les alertes le moment venu.
// ---------------------------------------------------------------------------
function TestEmailSection() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [recipient, setRecipient] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<TestEmailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const defaultEmail = user?.email ?? '';
  const effectiveEmail = recipient.trim() || defaultEmail;

  const onSend = async () => {
    setSending(true);
    setResult(null);
    setError(null);
    try {
      // On envoie le champ uniquement s il a ete saisi (sinon backend prend user.email)
      const r = await alertsApi.testEmail(recipient.trim() || undefined);
      setResult(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <CardHeader
        image={<Send24Regular primaryFill={tokens.colorBrandForeground1}/>}
        header={<Title3>{t('alerts.test.title')}</Title3>}
      />
      <Body1 style={{ display: 'block', marginBottom: 16 }}>
        {t('alerts.test.description')}
      </Body1>

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 280 }}>
          <Label htmlFor="test-email-input" style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600 }}>
            {t('alerts.test.inputLabel')}
          </Label>
          <Input
            id="test-email-input"
            type="email"
            value={recipient}
            onChange={(_, data) => setRecipient(data.value)}
            placeholder={defaultEmail || t('alerts.test.inputPlaceholder')}
            disabled={sending}
            style={{ width: '100%' }}
          />
          <Caption1 style={{
            display: 'block',
            marginTop: 6,
            color: tokens.colorNeutralForeground3,
          }}>
            {recipient.trim()
              ? t('alerts.test.willSendTo', { email: recipient.trim() })
              : t('alerts.test.useSession', { email: defaultEmail || '(inconnu)' })}
          </Caption1>
        </div>

        <Button
          appearance="primary"
          icon={sending ? <Spinner size="tiny" /> : <Send24Regular />}
          onClick={onSend}
          disabled={sending || !effectiveEmail}
        >
          {sending ? t('alerts.test.submitting') : t('alerts.test.submit')}
        </Button>
      </div>

      {/* Resultat */}
      {result && (
        <MessageBar intent="success" style={{ marginTop: 16 }}>
          <MessageBarBody>
            <MessageBarTitle>
              <CheckmarkCircle24Regular style={{ verticalAlign: 'middle', marginRight: 6 }} />
              {t('alerts.test.successTitle')}
            </MessageBarTitle>
            {t('alerts.test.successBody', { recipient: result.recipient, testId: result.testId })}
          </MessageBarBody>
        </MessageBar>
      )}

      {error && (
        <MessageBar intent="error" style={{ marginTop: 16 }}>
          <MessageBarBody>
            <MessageBarTitle>{t('alerts.test.errorTitle')}</MessageBarTitle>
            {error}
          </MessageBarBody>
        </MessageBar>
      )}
    </Card>
  );
}

function ProUpsellBanner({ currentPlan }: { currentPlan: string }) {
  const { t } = useTranslation();
  const currentLabel = currentPlan || t('billing.plan.trial');
  return (
    <Card style={{
      background: 'linear-gradient(135deg, #FFF3D6 0%, #FFE8B5 100%)',
      border: '1.5px solid #D9842B',
      padding: 20,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
        <Sparkle24Filled style={{ color: '#B45309' }} />
        <div style={{ flex: 1 }}>
          <Title3 style={{ color: '#8A6308', margin: 0 }}>
            {t('alerts.upsell.title')}
          </Title3>
          <Caption1 style={{ color: '#8A6308', display: 'block', marginTop: 4 }}>
            {t('alerts.upsell.currentPlan', { plan: currentLabel })}
          </Caption1>
        </div>
        <Link
          to="/billing"
          style={{
            background: '#1F3D5C',
            color: '#fff',
            padding: '10px 18px',
            borderRadius: 8,
            fontWeight: 700,
            fontSize: 14,
            textDecoration: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {t('alerts.upsell.cta')}
        </Link>
      </div>
      <MessageBar intent="info" style={{ background: 'rgba(255,255,255,.6)' }}>
        <MessageBarBody style={{ fontSize: 13 }}>
          {t('alerts.upsell.perks')}
        </MessageBarBody>
      </MessageBar>
    </Card>
  );
}
